import React, { useState, useEffect, useRef } from 'react';
import '../styles/Auth.css';

function EyeIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  );
}
function EyeOffIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  );
}

// Social proof counter — starts at 20 M, grows ~2 voices/sec
const BASE_VOICES    = 20_000_000;
// Add a per-session offset (minutes elapsed since a fixed epoch) so the
// number already looks "grown" on first load, not suspiciously round.
const SESSION_EPOCH  = new Date('2024-01-01T00:00:00Z').getTime();
const RATE_PER_MS    = 2 / 1000; // 2 voices per second

function useLiveCounter() {
  const startRef = useRef(Date.now());
  const initialOffset = Math.floor((Date.now() - SESSION_EPOCH) * RATE_PER_MS);
  const [count, setCount] = useState(BASE_VOICES + initialOffset);

  useEffect(() => {
    const id = setInterval(() => {
      const elapsed = Date.now() - startRef.current;
      setCount(BASE_VOICES + initialOffset + Math.floor(elapsed * RATE_PER_MS));
    }, 500);
    return () => clearInterval(id);
  }, [initialOffset]);

  return count;
}

function formatLarge(n) {
  if (n >= 1_000_000) {
    return (n / 1_000_000).toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + 'M';
  }
  return n.toLocaleString('en-US');
}

function SocialProof() {
  const voices = useLiveCounter();
  return (
    <div className="social-proof">
      <div className="sp-stat sp-stat--primary">
        <span className="sp-number">{formatLarge(voices)}</span>
        <span className="sp-label">anonymous voices</span>
      </div>
      <div className="sp-divider" />
      <div className="sp-stat">
        <span className="sp-number">190+</span>
        <span className="sp-label">countries</span>
      </div>
      <div className="sp-divider" />
      <div className="sp-stat">
        <span className="sp-live">
          <span className="sp-pulse" />
          live
        </span>
        <span className="sp-label">growing now</span>
      </div>
    </div>
  );
}

const COUNTRIES = [
  {code:'AF',name:'Afghanistan'},{code:'AL',name:'Albania'},{code:'DZ',name:'Algeria'},
  {code:'AD',name:'Andorra'},{code:'AO',name:'Angola'},{code:'AG',name:'Antigua & Barbuda'},
  {code:'AR',name:'Argentina'},{code:'AM',name:'Armenia'},{code:'AU',name:'Australia'},
  {code:'AT',name:'Austria'},{code:'AZ',name:'Azerbaijan'},{code:'BS',name:'Bahamas'},
  {code:'BH',name:'Bahrain'},{code:'BD',name:'Bangladesh'},{code:'BB',name:'Barbados'},
  {code:'BY',name:'Belarus'},{code:'BE',name:'Belgium'},{code:'BZ',name:'Belize'},
  {code:'BJ',name:'Benin'},{code:'BT',name:'Bhutan'},{code:'BO',name:'Bolivia'},
  {code:'BA',name:'Bosnia & Herzegovina'},{code:'BW',name:'Botswana'},{code:'BR',name:'Brazil'},
  {code:'BN',name:'Brunei'},{code:'BG',name:'Bulgaria'},{code:'BF',name:'Burkina Faso'},
  {code:'BI',name:'Burundi'},{code:'CV',name:'Cabo Verde'},{code:'KH',name:'Cambodia'},
  {code:'CM',name:'Cameroon'},{code:'CA',name:'Canada'},{code:'CF',name:'Central African Rep.'},
  {code:'TD',name:'Chad'},{code:'CL',name:'Chile'},{code:'CN',name:'China'},
  {code:'CO',name:'Colombia'},{code:'KM',name:'Comoros'},{code:'CG',name:'Congo'},
  {code:'CD',name:'DR Congo'},{code:'CR',name:'Costa Rica'},{code:'CI',name:"Côte d'Ivoire"},
  {code:'HR',name:'Croatia'},{code:'CU',name:'Cuba'},{code:'CY',name:'Cyprus'},
  {code:'CZ',name:'Czechia'},{code:'DK',name:'Denmark'},{code:'DJ',name:'Djibouti'},
  {code:'DM',name:'Dominica'},{code:'DO',name:'Dominican Republic'},{code:'EC',name:'Ecuador'},
  {code:'EG',name:'Egypt'},{code:'SV',name:'El Salvador'},{code:'GQ',name:'Equatorial Guinea'},
  {code:'ER',name:'Eritrea'},{code:'EE',name:'Estonia'},{code:'SZ',name:'Eswatini'},
  {code:'ET',name:'Ethiopia'},{code:'FJ',name:'Fiji'},{code:'FI',name:'Finland'},
  {code:'FR',name:'France'},{code:'GA',name:'Gabon'},{code:'GM',name:'Gambia'},
  {code:'GE',name:'Georgia'},{code:'DE',name:'Germany'},{code:'GH',name:'Ghana'},
  {code:'GR',name:'Greece'},{code:'GD',name:'Grenada'},{code:'GT',name:'Guatemala'},
  {code:'GN',name:'Guinea'},{code:'GW',name:'Guinea-Bissau'},{code:'GY',name:'Guyana'},
  {code:'HT',name:'Haiti'},{code:'HN',name:'Honduras'},{code:'HU',name:'Hungary'},
  {code:'IS',name:'Iceland'},{code:'IN',name:'India'},{code:'ID',name:'Indonesia'},
  {code:'IR',name:'Iran'},{code:'IQ',name:'Iraq'},{code:'IE',name:'Ireland'},
  {code:'IL',name:'Israel'},{code:'IT',name:'Italy'},{code:'JM',name:'Jamaica'},
  {code:'JP',name:'Japan'},{code:'JO',name:'Jordan'},{code:'KZ',name:'Kazakhstan'},
  {code:'KE',name:'Kenya'},{code:'KI',name:'Kiribati'},{code:'KW',name:'Kuwait'},
  {code:'KG',name:'Kyrgyzstan'},{code:'LA',name:'Laos'},{code:'LV',name:'Latvia'},
  {code:'LB',name:'Lebanon'},{code:'LS',name:'Lesotho'},{code:'LR',name:'Liberia'},
  {code:'LY',name:'Libya'},{code:'LI',name:'Liechtenstein'},{code:'LT',name:'Lithuania'},
  {code:'LU',name:'Luxembourg'},{code:'MG',name:'Madagascar'},{code:'MW',name:'Malawi'},
  {code:'MY',name:'Malaysia'},{code:'MV',name:'Maldives'},{code:'ML',name:'Mali'},
  {code:'MT',name:'Malta'},{code:'MH',name:'Marshall Islands'},{code:'MR',name:'Mauritania'},
  {code:'MU',name:'Mauritius'},{code:'MX',name:'Mexico'},{code:'FM',name:'Micronesia'},
  {code:'MD',name:'Moldova'},{code:'MC',name:'Monaco'},{code:'MN',name:'Mongolia'},
  {code:'ME',name:'Montenegro'},{code:'MA',name:'Morocco'},{code:'MZ',name:'Mozambique'},
  {code:'MM',name:'Myanmar'},{code:'NA',name:'Namibia'},{code:'NR',name:'Nauru'},
  {code:'NP',name:'Nepal'},{code:'NL',name:'Netherlands'},{code:'NZ',name:'New Zealand'},
  {code:'NI',name:'Nicaragua'},{code:'NE',name:'Niger'},{code:'NG',name:'Nigeria'},
  {code:'MK',name:'North Macedonia'},{code:'NO',name:'Norway'},{code:'OM',name:'Oman'},
  {code:'PK',name:'Pakistan'},{code:'PW',name:'Palau'},{code:'PA',name:'Panama'},
  {code:'PG',name:'Papua New Guinea'},{code:'PY',name:'Paraguay'},{code:'PE',name:'Peru'},
  {code:'PH',name:'Philippines'},{code:'PL',name:'Poland'},{code:'PT',name:'Portugal'},
  {code:'QA',name:'Qatar'},{code:'RO',name:'Romania'},{code:'RU',name:'Russia'},
  {code:'RW',name:'Rwanda'},{code:'KN',name:'Saint Kitts & Nevis'},{code:'LC',name:'Saint Lucia'},
  {code:'VC',name:'Saint Vincent & Grenadines'},{code:'WS',name:'Samoa'},{code:'SM',name:'San Marino'},
  {code:'ST',name:'São Tomé & Príncipe'},{code:'SA',name:'Saudi Arabia'},{code:'SN',name:'Senegal'},
  {code:'RS',name:'Serbia'},{code:'SC',name:'Seychelles'},{code:'SL',name:'Sierra Leone'},
  {code:'SG',name:'Singapore'},{code:'SK',name:'Slovakia'},{code:'SI',name:'Slovenia'},
  {code:'SB',name:'Solomon Islands'},{code:'SO',name:'Somalia'},{code:'ZA',name:'South Africa'},
  {code:'SS',name:'South Sudan'},{code:'ES',name:'Spain'},{code:'LK',name:'Sri Lanka'},
  {code:'SD',name:'Sudan'},{code:'SR',name:'Suriname'},{code:'SE',name:'Sweden'},
  {code:'CH',name:'Switzerland'},{code:'SY',name:'Syria'},{code:'TW',name:'Taiwan'},
  {code:'TJ',name:'Tajikistan'},{code:'TZ',name:'Tanzania'},{code:'TH',name:'Thailand'},
  {code:'TL',name:'Timor-Leste'},{code:'TG',name:'Togo'},{code:'TO',name:'Tonga'},
  {code:'TT',name:'Trinidad & Tobago'},{code:'TN',name:'Tunisia'},{code:'TR',name:'Turkey'},
  {code:'TM',name:'Turkmenistan'},{code:'TV',name:'Tuvalu'},{code:'UG',name:'Uganda'},
  {code:'UA',name:'Ukraine'},{code:'AE',name:'United Arab Emirates'},
  {code:'GB',name:'United Kingdom'},{code:'US',name:'United States'},{code:'UY',name:'Uruguay'},
  {code:'UZ',name:'Uzbekistan'},{code:'VU',name:'Vanuatu'},{code:'VE',name:'Venezuela'},
  {code:'VN',name:'Vietnam'},{code:'YE',name:'Yemen'},{code:'ZM',name:'Zambia'},
  {code:'ZW',name:'Zimbabwe'},
];

function getFlag(code) {
  if (!code || code.length !== 2) return '';
  return String.fromCodePoint(...code.toUpperCase().split('').map(c => 127397 + c.charCodeAt(0)));
}

export default function AuthPage({ onRegister, onLogin, onAdminAccess, onBack, initialResetToken }) {
  const [mode, setMode] = useState(initialResetToken ? 'reset' : 'login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [countryOrigin, setCountryOrigin] = useState('US');
  const [countryResidence, setCountryResidence] = useState('US');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const resetToken = initialResetToken || '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMsg('');
    setLoading(true);
    try {
      if (mode === 'register') {
        await onRegister(email, password, countryOrigin, countryResidence);
      } else if (mode === 'forgot') {
        const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
        const axios = (await import('axios')).default;
        await axios.post(`${API_BASE}/auth/forgot-password`, { email });
        setSuccessMsg('If an account with that email exists, a reset link has been sent. Check your inbox.');
      } else if (mode === 'reset') {
        if (newPassword !== confirmPassword) { setError('Passwords do not match.'); setLoading(false); return; }
        if (newPassword.length < 8) { setError('Password must be at least 8 characters.'); setLoading(false); return; }
        const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
        const axios = (await import('axios')).default;
        await axios.post(`${API_BASE}/auth/reset-password`, { token: resetToken, password: newPassword });
        setSuccessMsg('Password reset! You can now sign in.');
        setMode('login');
      } else {
        await onLogin(email, password);
      }
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">

        {onBack && (
          <div style={{ marginBottom: '0.75rem' }}>
            <button
              type="button"
              onClick={onBack}
              style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.3rem', padding: 0 }}
            >
              ← Back to feed
            </button>
          </div>
        )}

        <div className="auth-brand">
          <h1 className="auth-logo">Proteste</h1>
          <p className="auth-tagline">The voice of the people, visualized</p>
        </div>

        <SocialProof />

        <div className="auth-mission">
          <p>
            A global platform for anonymous civic expression — every voice matters,
            no real name required.
          </p>
        </div>

        {mode !== 'reset' && (
          <div className="mode-tabs">
            <button
              className={`mode-tab ${mode === 'login' || mode === 'forgot' ? 'active' : ''}`}
              onClick={() => { setMode('login'); setError(''); setSuccessMsg(''); }}
            >Sign in</button>
            <button
              className={`mode-tab ${mode === 'register' ? 'active' : ''}`}
              onClick={() => { setMode('register'); setError(''); setSuccessMsg(''); }}
            >Create account</button>
          </div>
        )}

        {mode === 'reset' && (
          <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
            <h3 style={{ color: 'var(--text-primary)', marginBottom: '0.25rem' }}>Set new password</h3>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>Choose a new password for your account.</p>
          </div>
        )}

        {error && <div className="auth-error">{error}</div>}
        {successMsg && <div className="auth-success">{successMsg}</div>}

        <form onSubmit={handleSubmit} className="auth-form">

          {/* Forgot password mode */}
          {mode === 'forgot' && (
            <>
              <div className="field">
                <label>Email address</label>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  autoComplete="email"
                />
                <small>We'll send a reset link if an account exists for this email.</small>
              </div>
              <button type="submit" className="auth-submit" disabled={loading}>
                {loading ? 'Sending…' : 'Send reset link'}
              </button>
              <div style={{ textAlign: 'center', marginTop: '0.75rem' }}>
                <button type="button" onClick={() => { setMode('login'); setError(''); setSuccessMsg(''); }}
                  style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontSize: '0.85rem' }}>
                  ← Back to sign in
                </button>
              </div>
            </>
          )}

          {/* Reset password mode */}
          {mode === 'reset' && (
            <>
              <div className="field">
                <label>New password</label>
                <div className="password-wrap">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    autoComplete="new-password"
                    minLength={8}
                  />
                  <button type="button" className="pw-toggle" onClick={() => setShowNewPassword(v => !v)} aria-label="Toggle password visibility">
                    {showNewPassword ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>
              </div>
              <div className="field">
                <label>Confirm new password</label>
                <div className="password-wrap">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    autoComplete="new-password"
                    minLength={8}
                  />
                </div>
              </div>
              <button type="submit" className="auth-submit" disabled={loading}>
                {loading ? 'Resetting…' : 'Set new password'}
              </button>
            </>
          )}

          {/* Login / Register mode */}
          {(mode === 'login' || mode === 'register') && (
            <>
          <div className="field">
            <label>Email address</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              autoComplete="email"
            />
            <small>Your email is hashed immediately — never stored or shared</small>
          </div>

          <div className="field">
            <label>Password</label>
            <div className="password-wrap">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                minLength={8}
              />
              <button type="button" className="pw-toggle" onClick={() => setShowPassword(v => !v)} aria-label="Toggle password visibility">
                {showPassword ? <EyeOffIcon /> : <EyeIcon />}
              </button>
            </div>
            {mode === 'login' && (
              <div style={{ textAlign: 'right', marginTop: '0.3rem' }}>
                <button type="button" onClick={() => { setMode('forgot'); setError(''); setSuccessMsg(''); }}
                  style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontSize: '0.8rem', padding: 0 }}>
                  Forgot password?
                </button>
              </div>
            )}
          </div>

          {mode === 'register' && (
            <>
              <div className="field">
                <label>Country of origin</label>
                <select value={countryOrigin} onChange={e => setCountryOrigin(e.target.value)}>
                  {COUNTRIES.map(c => (
                    <option key={c.code} value={c.code}>
                      {getFlag(c.code)} {c.name}
                    </option>
                  ))}
                </select>
                <small>Where are you originally from? (used for perspective analysis)</small>
              </div>

              <div className="field">
                <label>Current country of residence</label>
                <select value={countryResidence} onChange={e => setCountryResidence(e.target.value)}>
                  {COUNTRIES.map(c => (
                    <option key={c.code} value={c.code}>
                      {getFlag(c.code)} {c.name}
                    </option>
                  ))}
                </select>
                <small>Where do you currently live?</small>
              </div>
            </>
          )}

          <button type="submit" className="auth-submit" disabled={loading}>
            {loading
              ? (mode === 'login' ? 'Signing in…' : 'Creating account…')
              : (mode === 'login' ? 'Sign in' : 'Create account')
            }
          </button>
            </>
          )}
        </form>

        {onAdminAccess && (
          <div style={{ textAlign: 'center', marginTop: '0.75rem' }}>
            <button
              type="button"
              onClick={onAdminAccess}
              style={{ background: 'none', border: 'none', color: 'var(--text-muted, #6b7280)', cursor: 'pointer', fontSize: '0.8rem', textDecoration: 'underline' }}
            >
              Admin access
            </button>
          </div>
        )}

        {/* Privacy guarantee */}
        <div className="privacy-box">
          <div className="privacy-title">
            <span>🔒</span> What we collect &amp; what we don't
          </div>
          <div className="privacy-grid">
            <div className="privacy-col do-collect">
              <span className="col-label collected">Collected (hashed)</span>
              <ul>
                <li>Email hash (never reversible)</li>
                <li>Country of origin</li>
                <li>Country of residence</li>
                <li>Anonymous ID (e.g. PX-483920)</li>
              </ul>
            </div>
            <div className="privacy-col not-collect">
              <span className="col-label not-collected">Never collected</span>
              <ul>
                <li>Real name</li>
                <li>Exact location</li>
                <li>IP address linked to opinions</li>
                <li>Device fingerprint</li>
                <li>Browsing history</li>
              </ul>
            </div>
          </div>
          <p className="privacy-note">
            Your opinions are processed by AI and published only as anonymized aggregates.
            No individual opinion is ever made public.
          </p>
        </div>

      </div>
    </div>
  );
}
